/**
 * Métricas Admin Data / Compare (contrato unificación de promedios §5 / §9).
 * - Seguro: preferir INSURANCE_AVG / INSURANCE_VPD
 * - Regular: VPD / NON_PROFIT_VPD (Chargeable incluye membresía en backend)
 * - AVG_*: siempre número con 2 decimales; 0 no queda vacío
 */

export function toNum(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

export function round2(v) {
  return Math.round((toNum(v) + Number.EPSILON) * 100) / 100
}

/** Formato UI: siempre string numérico con 2 decimales (incluye 0.00). */
export function formatAvg2(v) {
  return round2(v).toFixed(2)
}

/**
 * Lee AVG_* con fallback y formatea a 2 decimales.
 * @param {Object} row
 * @param {string} key
 */
export function readAvgField(row, key) {
  if (!row) return formatAvg2(0)
  const v = row[key]
  if (v === '' || v == null) return formatAvg2(0)
  return formatAvg2(v)
}

/**
 * Filas de la tabla stacked de averages (carril regular).
 * Chargeable_VPD = VPD (incluye membresía según contrato).
 */
export function buildRegularAveragesRow(datarda) {
  const d = datarda || {}
  const totals = d.TOTALS || {}
  return {
    total_income: toNum(totals.income),
    Total_Expences: toNum(totals.expenses),
    Total_Net_Income: toNum(totals.net_income),
    Total_Income_Cash: toNum(totals.cash),
    Total_Income_CC: toNum(totals.cc),
    Patient_Avg_Charge: formatAvg2(d.AVG_TOTAL),
    NC_Avg_Charge: formatAvg2(d.AVG_NC),
    NP_Avg_Charge: formatAvg2(d.AVG_NP),
    NC_and_NP_AVg_Charge: formatAvg2(d.AVG_NC_NP),
    Chargeable_VPD: toNum(d.VPD),
    Less_Chargeable_VPD: toNum(d.NON_PROFIT_VPD),
    Total_VPD: toNum(d.TOTAL_VPD),
    Profit_Avg: formatAvg2(d.PROFIT_AVG),
    Non_Profit_Avg: formatAvg2(d.NON_PROFIT_AVG),
    Count_30_NP: toNum(d.COUNT_30_NP),
    Count_30_NC: toNum(d.COUNT_30_NC),
  }
}

/**
 * Filas averages carril seguro: INSURANCE_* con fallback a VPD/PROFIT legacy.
 */
export function buildInsuranceAveragesRow(datarda) {
  const d = datarda || {}
  const totals = d.TOTALS || {}
  const insuranceAvg =
    d.INSURANCE_AVG != null && d.INSURANCE_AVG !== ''
      ? d.INSURANCE_AVG
      : d.PROFIT_AVG != null && d.PROFIT_AVG !== ''
      ? d.PROFIT_AVG
      : d.AVG_TOTAL
  const insuranceVpd =
    d.INSURANCE_VPD != null && d.INSURANCE_VPD !== ''
      ? d.INSURANCE_VPD
      : d.VPD

  return {
    total_income: toNum(totals.income),
    Total_Expences: toNum(totals.expenses),
    Total_Net_Income: toNum(totals.net_income),
    Total_Income_Cash: toNum(totals.cash),
    Total_Income_CC: toNum(totals.cc),
    Patient_Avg_Charge: formatAvg2(d.AVG_TOTAL),
    NC_Avg_Charge: formatAvg2(d.AVG_NC),
    NP_Avg_Charge: formatAvg2(d.AVG_NP),
    NC_and_NP_AVg_Charge: formatAvg2(d.AVG_NC_NP),
    Insurance_Avg: formatAvg2(insuranceAvg),
    Insurance_VPD: toNum(insuranceVpd),
    Total_VPD: toNum(d.TOTAL_VPD != null ? d.TOTAL_VPD : insuranceVpd),
  }
}

/**
 * Campos compare facilities (regular).
 */
export function formatCompareFacilityRegular(facilityRow) {
  const f = facilityRow || {}
  const totals = f.TOTALS || {}
  return {
    facility: f.facility,
    TOTALS: {
      income: toNum(totals.income),
      expenses: toNum(totals.expenses),
      net_income: toNum(totals.net_income),
      cash: toNum(totals.cash),
      cc: toNum(totals.cc),
    },
    AVG_TOTAL: formatAvg2(f.AVG_TOTAL),
    AVG_NC: formatAvg2(f.AVG_NC),
    AVG_NP: formatAvg2(f.AVG_NP),
    AVG_NC_NP: formatAvg2(f.AVG_NC_NP),
    VPD: toNum(f.VPD),
    NON_PROFIT_VPD: toNum(f.NON_PROFIT_VPD),
    TOTAL_VPD: toNum(f.TOTAL_VPD),
    PROFIT_AVG: formatAvg2(f.PROFIT_AVG),
    NON_PROFIT_AVG: formatAvg2(f.NON_PROFIT_AVG),
    COUNT_30_NP: toNum(f.COUNT_30_NP),
    COUNT_30_NC: toNum(f.COUNT_30_NC),
  }
}

/**
 * Campos compare facilities (seguro).
 */
export function formatCompareFacilityInsurance(facilityRow) {
  const f = facilityRow || {}
  const totals = f.TOTALS || {}
  const insuranceAvg =
    f.INSURANCE_AVG != null && f.INSURANCE_AVG !== ''
      ? f.INSURANCE_AVG
      : f.PROFIT_AVG != null && f.PROFIT_AVG !== ''
      ? f.PROFIT_AVG
      : f.AVG_TOTAL
  const insuranceVpd =
    f.INSURANCE_VPD != null && f.INSURANCE_VPD !== '' ? f.INSURANCE_VPD : f.VPD

  return {
    facility: f.facility,
    TOTALS: {
      income: toNum(totals.income),
      expenses: toNum(totals.expenses),
      net_income: toNum(totals.net_income),
      cash: toNum(totals.cash),
      cc: toNum(totals.cc),
    },
    AVG_TOTAL: formatAvg2(f.AVG_TOTAL),
    AVG_NC: formatAvg2(f.AVG_NC),
    AVG_NP: formatAvg2(f.AVG_NP),
    AVG_NC_NP: formatAvg2(f.AVG_NC_NP),
    INSURANCE_AVG: formatAvg2(insuranceAvg),
    INSURANCE_VPD: toNum(insuranceVpd),
    TOTAL_VPD: toNum(f.TOTAL_VPD != null ? f.TOTAL_VPD : insuranceVpd),
  }
}

/**
 * Pondera un promedio por su VPD (Σ avg×vpd / Σ vpd).
 * @param {Array} months
 * @param {string} avgKey
 * @param {string} vpdKey
 */
export function weightedAverageByVpd(months, avgKey, vpdKey) {
  let weighted = 0
  let weight = 0
  ;(months || []).forEach(m => {
    const w = toNum(m?.[vpdKey])
    if (w <= 0) return
    weighted += toNum(m?.[avgKey]) * w
    weight += w
  })
  return weight > 0 ? round2(weighted / weight) : 0
}
